/**
 * AutoPageSEO
 *
 * Renders FAQ (and optionally intro) blocks automatically after every page's
 * content — without any changes needed in individual page components.
 *
 * This component is mounted once in App.js after <Routes>.
 * It reads from PageContentContext (which refreshes on every route change)
 * and renders a PageFAQ accordion whenever the backend returns FAQs for
 * the current path.
 *
 * Pages that manage their own FAQ (listed in SELF_MANAGED_PATHS) are excluded
 * to avoid duplicate FAQ sections. Add a pattern here if a future page
 * implements its own FAQ section.
 *
 * For new routes: zero code needed. Just extend get_page_content_for_path()
 * in config/views.py with the new path — the frontend picks it up automatically.
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import { usePageContentContext } from '../context/PageContentContext';
import PageFAQ from './seo/PageFAQ';

/**
 * Regex patterns for paths that render their own FAQ section.
 * Global FAQ is skipped for these to avoid duplication.
 */
const SELF_MANAGED_PATHS = [
  /^\/$/, // Homepage — uses AIPromptOptimizedFAQ
  /^\/business\/.+/, // BusinessDetailsNew — renders owner-authored PageFAQ from business.properties.faqs
  /^\/businesses\/.+/, // CityListingPage — has styled FAQSection
  /^\/categories\/.+/, // CategoryListingPage — has styled FAQSection
  /^\/blog\/.+/, // BlogPost — renders PageFAQ with custom title
  /^\/deals-list/, // DealListNew renders PageFAQ inside PageWrapper
  /^\/all-listings/, // BusinessListNew renders PageFAQ inside PageWrapper
  /^\/indian-business-directory-australia/, // HeritageTagListingPage (national mode)
  /^\/pakistani-business-directory-australia/, // HeritageTagListingPage (national mode)
  /^\/bangladeshi-business-directory-australia/, // HeritageTagListingPage (national mode)
];

const AutoPageSEO = () => {
  const content = usePageContentContext();
  const { pathname } = useLocation();

  // No content or no FAQs for this path → render nothing
  if (!content?.faqs?.length) return null;

  // Page manages its own FAQ → skip global rendering
  if (SELF_MANAGED_PATHS.some(re => re.test(pathname))) return null;

  return <PageFAQ faqs={content.faqs} />;
};

export default AutoPageSEO;
